import CustomLoadingButton from "@/components/Button/CustomLoadingButton";
import { IS_DEV } from "@/constants";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { TableCellProps, TextField, Tooltip } from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import * as React from "react";
import Cell from "./Cell";

/**
 * Edit mode: turn it on, every editable cell in the loaded rows becomes a field,
 * change as many as you like, save once.
 *
 * The whole design rests on one decision — **the drafts do not live in React
 * state**. They live in a store held by `CustomTable` in a ref, and each cell
 * subscribes to its own entry through `useSyncExternalStore`. A keystroke
 * re-renders one cell.
 *
 * That is not premature optimisation, it is the difference between working and
 * not. Had the list owned the drafts, every keystroke would re-render
 * `CustomTable`, whose `sx` is rebuilt as a fresh object literal each render
 * (`CustomTable/index.tsx`, the `...layoutSx` spread) so Emotion re-serialises
 * ~26 selectors; and every row, because `rowComponentProps` is memoised on a
 * prop the lists pass as an inline object literal. On Creatives each of those
 * rows runs `useGetBwCreativeDetails`, a `<video>` and a full template render.
 *
 * Putting the drafts in a *context value* instead has the same problem for a
 * subtler reason: a new Map in the value invalidates every consumer, whichever
 * row changed. Hence the constant context value below, carrying the store
 * itself rather than its contents.
 */

export type RowId = string | number;

/** A row's saved value for one column, and what the user has typed over it. */
interface DraftEntry {
  value: string;
  original: string;
  /** Fails the value itself. Blocks saving, because sending it is pointless. */
  error?: string;
  /**
   * The server refused this row on the last save. Shown exactly like a
   * validation error, but deliberately does NOT block the next Save — a rejected
   * row is usually retried, not retyped, and treating the two the same left the
   * only way forward as discarding the work.
   */
  failure?: string;
  /** Captured at edit time so a failure can name the row, not its id. */
  label?: string;
}

export interface EditChange {
  id: RowId;
  /** Only the columns actually changed on this row. */
  values: Record<string, string | null>;
}

export interface EditFailure {
  id: RowId;
  error: string;
}

export interface EditableField<TRow = any> {
  /** The saved value, as it sits on the row. */
  read: (row: TRow) => string | null | undefined;
  /**
   * Collapse everything that means "no value" onto one shape, and hand back what
   * should be SENT.
   *
   * Runs before the dirty comparison **and before `validate`**, and the second
   * one is the load-bearing half. yup 0.32's `matches` is
   * `isAbsent(value) || (value === '' && excludeEmptyString) || value.search(re)`,
   * and `excludeEmptyString` defaults to false — so `null` passes a URL rule and
   * `""` fails it. Without normalising first, clearing a value is not something
   * the user can do: the field goes empty, validation rejects it, and Save stays
   * disabled with no way forward.
   */
  normalize?: (value: string) => string | null;
  /** Message to show, or undefined when the value is acceptable. */
  validate?: (value: string | null, row: TRow) => string | undefined;
  /**
   * Whether THIS row's cell can be edited. Rows that return false render their
   * view-mode content in edit mode, disabled.
   */
  editable?: (row: TRow) => boolean;
  /** Shown in the empty field, and (greyed) in view mode when there is no value. */
  placeholder?: string;
  /** View-mode rendering. Defaults to the raw value. */
  render?: (value: string | null, row: TRow) => React.ReactNode;
  /** Why this row cannot be edited, shown on its disabled cell. */
  disabledReason?: (row: TRow) => string | undefined;
}

export interface TableEditingConfig<TRow = any> {
  /** Editable columns, by the `id` of their header. */
  fields: Record<string, EditableField<TRow>>;
  /**
   * Persist every changed row. See `EditChange`.
   *
   * - **Throws** → every draft is kept and the table stays in edit mode.
   * - **Resolves with `failed`** → the rows that saved are cleared, the ones
   *   that did not keep their drafts and show their error.
   * - **Resolves with nothing** → everything saved, edit mode closes.
   */
  onSave: (changes: EditChange[]) => Promise<void | { failed?: EditFailure[] }>;
  /** Names a row in an error. Without it errors can only say the id. */
  getRowLabel?: (row: TRow) => string;
  /**
   * Identity for a row's drafts. Defaults to `row.id`.
   *
   * Deliberately NOT `CustomTable`'s `getRowKey`, even though that exists for
   * the same problem: its signature takes the row's index, and an index changes
   * when the table re-sorts. A draft keyed on one would be orphaned by a sort
   * click. Lists whose `row.id` is not unique across the list — the in-campaign
   * line items, drawn from three tables with independent id sequences — must
   * pass an index-free id here.
   */
  getRowId?: (row: TRow) => RowId;
}

export interface EditSummary {
  dirtyCount: number;
  /** Values that cannot be sent. Blocks Save. */
  invalidCount: number;
  /** Rows the server refused last time. Does NOT block Save — retrying is the point. */
  failedCount: number;
  /** The row an invalid value is on, for the toolbar's message. */
  firstInvalid?: string;
  firstFailed?: string;
}

const EMPTY_SUMMARY: EditSummary = {
  dirtyCount: 0,
  invalidCount: 0,
  failedCount: 0,
};

// ---------------------------------------------------------------------------
// the store
// ---------------------------------------------------------------------------

export interface DraftStore<TRow = any> {
  getEntry(rowId: RowId, columnId: string): DraftEntry | undefined;
  setValue(rowId: RowId, columnId: string, value: string, row: TRow): void;
  /** Re-run validation for a row without changing its value. */
  revalidate(rowId: RowId, row: TRow): void;
  clearAll(): void;
  clearRows(ids: RowId[]): void;
  /** Drop drafts for rows that are no longer in the table. */
  prune(present: Set<RowId>): void;
  setFailures(failures: EditFailure[]): void;
  subscribeCell(rowId: RowId, columnId: string, fn: () => void): () => void;
  subscribeSummary(fn: () => void): () => void;
  getSummary(): EditSummary;
  changes(): EditChange[];
  /** Config changes between renders; the store reads it through this slot. */
  configure(config: TableEditingConfig<TRow>): void;
}

// The separator is escaped rather than written as a literal NUL. Both compile
// to the same U+0000 -- which is the point of choosing it, since no row id or
// column id can contain one -- but a raw NUL in the source makes git classify
// this file as binary, so it stops diffing and grep stops finding anything in
// it.
const cellKey = (rowId: RowId, columnId: string) => `${rowId}\0${columnId}`;

function createDraftStore<TRow>(): DraftStore<TRow> {
  const drafts = new Map<RowId, Map<string, DraftEntry>>();
  const cellListeners = new Map<string, Set<() => void>>();
  const summaryListeners = new Set<() => void>();
  let config: TableEditingConfig<TRow> | null = null;
  // Recomputed on every change and handed out by identity, because
  // `useSyncExternalStore` re-renders whenever the snapshot fails an `Object.is`
  // check — a fresh object each call would loop forever.
  let summary: EditSummary = EMPTY_SUMMARY;

  const notifyCell = (rowId: RowId, columnId: string) => {
    cellListeners.get(cellKey(rowId, columnId))?.forEach((fn) => fn());
  };

  const recomputeSummary = () => {
    let dirtyCount = 0;
    let invalidCount = 0;
    let failedCount = 0;
    let firstInvalid: string | undefined;
    let firstFailed: string | undefined;
    drafts.forEach((columns) => {
      let rowDirty = false;
      columns.forEach((entry) => {
        if (entry.value === entry.original) return;
        rowDirty = true;
        if (entry.error) {
          invalidCount += 1;
          if (!firstInvalid) firstInvalid = entry.label;
        } else if (entry.failure) {
          failedCount += 1;
          if (!firstFailed) firstFailed = entry.label;
        }
      });
      if (rowDirty) dirtyCount += 1;
    });
    summary = {
      dirtyCount,
      invalidCount,
      failedCount,
      firstInvalid,
      firstFailed,
    };
    summaryListeners.forEach((fn) => fn());
  };

  /** Validation and the sent value both go through `normalize` — see the field docs. */
  const evaluate = (columnId: string, value: string, row: TRow) => {
    const field = config?.fields[columnId];
    const normalized = field?.normalize ? field.normalize(value) : value;
    return {
      normalized,
      error: field?.validate?.(normalized, row),
    };
  };

  return {
    configure(next) {
      config = next;
    },

    getEntry(rowId, columnId) {
      return drafts.get(rowId)?.get(columnId);
    },

    setValue(rowId, columnId, value, row) {
      const { error } = evaluate(columnId, value, row);
      let columns = drafts.get(rowId);
      if (!columns) {
        columns = new Map();
        drafts.set(rowId, columns);
      }
      const existing = columns.get(columnId);
      // The original is captured ONCE, the first time this cell is touched, and
      // never re-read. Reading it live off `rowData` means a background refetch
      // silently un-dirties or re-dirties rows underneath the user.
      const original =
        existing?.original ??
        (config?.fields[columnId]?.read(row) ?? "") ??
        "";
      columns.set(columnId, {
        value,
        original: String(original ?? ""),
        error,
        // Touching the value drops the previous rejection: it described the
        // value that was sent, and this is no longer that value.
        failure: undefined,
        label: config?.getRowLabel?.(row),
      });
      notifyCell(rowId, columnId);
      recomputeSummary();
    },

    revalidate(rowId, row) {
      const columns = drafts.get(rowId);
      if (!columns) return;
      columns.forEach((entry, columnId) => {
        const { error } = evaluate(columnId, entry.value, row);
        if (error === entry.error) return;
        columns.set(columnId, { ...entry, error });
        notifyCell(rowId, columnId);
      });
      recomputeSummary();
    },

    clearAll() {
      const touched = Array.from(drafts.entries()).flatMap(([rowId, columns]) =>
        Array.from(columns.keys()).map((columnId) => [rowId, columnId] as const)
      );
      drafts.clear();
      touched.forEach(([rowId, columnId]) => notifyCell(rowId, columnId));
      recomputeSummary();
    },

    clearRows(ids) {
      ids.forEach((rowId) => {
        const columns = drafts.get(rowId);
        if (!columns) return;
        const columnIds = Array.from(columns.keys());
        drafts.delete(rowId);
        columnIds.forEach((columnId) => notifyCell(rowId, columnId));
      });
      recomputeSummary();
    },

    // A row can leave the table while its draft is still open — Creatives'
    // Delete button splices the row out of the list's own state — and an orphan
    // draft would still be sent on the next save, PATCHing a deleted record.
    prune(present) {
      const gone = Array.from(drafts.keys()).filter((id) => !present.has(id));
      if (!gone.length) return;
      this.clearRows(gone);
    },

    setFailures(failures) {
      failures.forEach(({ id, error }) => {
        const columns = drafts.get(id);
        if (!columns) return;
        columns.forEach((entry, columnId) => {
          // `failure`, not `error` — see the field's docs. Recording it as a
          // validation error would disable Save, leaving Discard as the only
          // way out of a row the server merely refused once.
          columns.set(columnId, { ...entry, failure: error });
          notifyCell(id, columnId);
        });
      });
      recomputeSummary();
    },

    subscribeCell(rowId, columnId, fn) {
      const key = cellKey(rowId, columnId);
      let set = cellListeners.get(key);
      if (!set) {
        set = new Set();
        cellListeners.set(key, set);
      }
      set.add(fn);
      return () => {
        set?.delete(fn);
        if (set && set.size === 0) cellListeners.delete(key);
      };
    },

    subscribeSummary(fn) {
      summaryListeners.add(fn);
      return () => {
        summaryListeners.delete(fn);
      };
    },

    getSummary() {
      return summary;
    },

    changes() {
      const out: EditChange[] = [];
      drafts.forEach((columns, id) => {
        const values: Record<string, string | null> = {};
        let any = false;
        columns.forEach((entry, columnId) => {
          if (entry.value === entry.original) return;
          any = true;
          const field = config?.fields[columnId];
          values[columnId] = field?.normalize
            ? field.normalize(entry.value)
            : entry.value;
        });
        if (any) out.push({ id, values });
      });
      return out;
    },
  };
}

// ---------------------------------------------------------------------------
// context
// ---------------------------------------------------------------------------

interface TableEditContextValue<TRow = any> {
  store: DraftStore<TRow>;
  config: TableEditingConfig<TRow>;
  isEditing: boolean;
  getRowId: (row: TRow) => RowId;
}

/**
 * `null` means this table has no edit mode — which is every table but the one,
 * and every row component rendered outside a `CustomTable`. `<EditableCell />`
 * falls back to plain view-mode rendering, so a row using it is safe to share
 * between an editable list and a read-only one.
 */
export const TableEditContext =
  React.createContext<TableEditContextValue | null>(null);

/** Row components read this to stand their own buttons down while editing. */
export function useIsTableEditing() {
  return React.useContext(TableEditContext)?.isEditing ?? false;
}

// ---------------------------------------------------------------------------
// the hook CustomTable calls
// ---------------------------------------------------------------------------

export interface TableEditing<TRow = any> {
  isEditing: boolean;
  isSaving: boolean;
  enter: () => void;
  /** Discards every draft. */
  cancel: () => void;
  save: () => void;
  store: DraftStore<TRow>;
  contextValue: TableEditContextValue<TRow>;
  /** Column ids that may not be hidden while editing. */
  editableColumnIds: string[];
}

export function useTableEditing<TRow>(
  config: TableEditingConfig<TRow> | undefined,
  rows: TRow[],
  getRowId: (row: TRow) => RowId
): TableEditing<TRow> | null {
  const [isEditing, setIsEditing] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const storeRef = React.useRef<DraftStore<TRow>>();
  if (!storeRef.current) storeRef.current = createDraftStore<TRow>();
  const store = storeRef.current;

  // Read through a ref rather than a dep, so a list passing its config as an
  // inline object literal — which every list will — does not rebuild anything.
  const configRef = React.useRef(config);
  configRef.current = config;
  if (config) store.configure(config);

  // `getRowId` likewise: lists build it inline, and the identity of the row-id
  // function is not a reason to tear down the store.
  const getRowIdRef = React.useRef(getRowId);
  getRowIdRef.current = getRowId;
  const stableGetRowId = React.useCallback(
    (row: TRow) => getRowIdRef.current(row),
    []
  );

  const rowsRef = React.useRef(rows);
  rowsRef.current = rows;

  // Rows come and go under an open edit — see `prune`.
  const rowIdKey = rows.map((row) => getRowId(row)).join("|");
  React.useEffect(() => {
    const ids = rowsRef.current.map((row) => stableGetRowId(row));
    const present = new Set(ids);
    // Two rows sharing an id share a draft, so typing in one fills the other.
    // Silent, and indistinguishable from a React bug when you hit it.
    if (IS_DEV && config && present.size !== ids.length) {
      console.warn(
        "[CustomTable] edit mode: two rows share a row id, so they share a " +
          "draft. Pass `getRowId` on the editing config."
      );
    }
    store.prune(present);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowIdKey, store, stableGetRowId]);

  const enter = React.useCallback(() => setIsEditing(true), []);

  const cancel = React.useCallback(() => {
    store.clearAll();
    setIsEditing(false);
  }, [store]);

  const save = React.useCallback(async () => {
    const changes = store.changes();
    if (!changes.length) return;
    setIsSaving(true);
    try {
      const result = await configRef.current?.onSave(changes);
      const failed = result?.failed ?? [];
      if (!failed.length) {
        store.clearAll();
        setIsEditing(false);
        return;
      }
      // Partial success: the rows that saved let go, the ones that did not keep
      // their drafts and carry their reason. Anything else loses the user's work
      // for rows that were never the problem.
      const failedIds = new Set(failed.map((failure) => failure.id));
      store.clearRows(
        changes.map((change) => change.id).filter((id) => !failedIds.has(id))
      );
      store.setFailures(failed);
    } finally {
      setIsSaving(false);
    }
  }, [store]);

  const editableColumnIds = React.useMemo(
    () => (config ? Object.keys(config.fields) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config ? Object.keys(config.fields).join("|") : ""]
  );

  // The value every cell consumes. It must NOT change on a keystroke, which is
  // why it carries the store rather than the drafts — see the note at the top.
  const contextValue = React.useMemo<TableEditContextValue<TRow>>(
    () => ({
      store,
      config: configRef.current as TableEditingConfig<TRow>,
      isEditing,
      getRowId: stableGetRowId,
    }),
    [store, isEditing, stableGetRowId]
  );

  if (!config) return null;

  return {
    isEditing,
    isSaving,
    enter,
    cancel,
    save,
    store,
    contextValue,
    editableColumnIds,
  };
}

/** Subscribes to the change counts alone — used by the toolbar, not by cells. */
export function useEditSummary(store: DraftStore | undefined): EditSummary {
  const subscribe = React.useCallback(
    (fn: () => void) => store?.subscribeSummary(fn) ?? (() => undefined),
    [store]
  );
  const snapshot = React.useCallback(
    () => store?.getSummary() ?? EMPTY_SUMMARY,
    [store]
  );
  return React.useSyncExternalStore(subscribe, snapshot, snapshot);
}

// ---------------------------------------------------------------------------
// the cell
// ---------------------------------------------------------------------------

/**
 * A `<Cell />` that becomes a field while the table is in edit mode.
 *
 * It renders **exactly one `<td>` either way**. Every width, truncation and
 * pinned-column rule in `CustomTable` is an `nth-of-type` selector gated on
 * `data-columns-match`, which counts the cells each row renders against the
 * header manifest — a cell that appeared or vanished with the mode would drop
 * the whole table to auto layout.
 *
 * Outside a table with edit mode configured it is exactly a `<Cell />`, so a row
 * component can be shared between an editable list and a read-only one.
 */
export default function EditableCell<TRow>({
  columnId,
  row,
  ...props
}: { columnId: string; row: TRow } & TableCellProps) {
  const context = React.useContext(TableEditContext) as
    | TableEditContextValue<TRow>
    | null;
  const field = context?.config?.fields[columnId];
  const rowId = context && field ? context.getRowId(row) : undefined;
  const store = context?.store;

  const subscribe = React.useCallback(
    (fn: () => void) =>
      store && rowId !== undefined
        ? store.subscribeCell(rowId, columnId, fn)
        : () => undefined,
    [store, rowId, columnId]
  );
  // Primitives only: `useSyncExternalStore` compares snapshots with `Object.is`,
  // so returning the entry object would re-render on every unrelated change.
  const draftValue = React.useCallback(
    () =>
      store && rowId !== undefined
        ? store.getEntry(rowId, columnId)?.value
        : undefined,
    [store, rowId, columnId]
  );
  // Both read the same way in the cell — the difference is only whether Save is
  // blocked, which the toolbar decides.
  //
  // Silent once the value is back to what it was. A draft that equals its
  // original is not a change: `recomputeSummary` skips it, so it is not counted
  // and does not block Save. Reporting it here as well would leave a cell
  // marked invalid while the toolbar said nothing was wrong and Save was live —
  // and typing into a field and undoing it is the ordinary way to end up there.
  const draftError = React.useCallback(() => {
    if (!store || rowId === undefined) return undefined;
    const entry = store.getEntry(rowId, columnId);
    if (!entry || entry.value === entry.original) return undefined;
    return entry.error ?? entry.failure;
  }, [store, rowId, columnId]);
  const value = React.useSyncExternalStore(subscribe, draftValue, draftValue);
  const error = React.useSyncExternalStore(subscribe, draftError, draftError);

  const saved = field ? field.read(row) ?? "" : "";
  const shown = value ?? String(saved ?? "");

  const editingThisRow =
    context?.isEditing && field && (field.editable?.(row) ?? true);

  if (!context?.isEditing || !field) {
    return (
      <Cell columnId={columnId} {...props}>
        {viewContent(field, String(saved ?? ""), row)}
      </Cell>
    );
  }

  if (!editingThisRow) {
    const reason = field.disabledReason?.(row);
    // A disabled FIELD, not a dash. In a column where every other cell is a box,
    // a bare dash reads as something failing to render rather than as a rule.
    return (
      <Cell columnId={columnId} {...props}>
        <TextField
          fullWidth
          disabled
          size="small"
          value={String(saved ?? "")}
          placeholder={reason ?? "Not applicable"}
          sx={FIELD_SX}
          inputProps={{ "aria-label": reason ?? "Not editable" }}
        />
      </Cell>
    );
  }

  return (
    <Cell columnId={columnId} {...props}>
      <TextField
        fullWidth
        size="small"
        value={shown}
        placeholder={field.placeholder}
        error={Boolean(error)}
        onChange={(event) =>
          store?.setValue(rowId as RowId, columnId, event.target.value, row)
        }
        sx={FIELD_SX}
        InputProps={{
          // An adornment rather than `helperText`, deliberately. A message that
          // appears and disappears as you type changes the row's height, and
          // every height change runs CustomTable's ResizeObserver, which walks
          // every row and every cell to re-check the column count.
          endAdornment: error ? (
            <InputAdornment position="end">
              <Tooltip title={error}>
                <ErrorOutlineIcon color="error" fontSize="small" />
              </Tooltip>
            </InputAdornment>
          ) : undefined,
        }}
      />
    </Cell>
  );
}

function viewContent<TRow>(
  field: EditableField<TRow> | undefined,
  saved: string,
  row: TRow
) {
  if (field?.render) return field.render(saved === "" ? null : saved, row);
  return saved;
}

/**
 * Brings the field down to something a table row can carry.
 *
 * `minHeight` is the one that matters, and `size="small"` does not touch it: the
 * app's theme pins `.MuiOutlinedInput-input` at **44px**
 * (`theme/overridesComponets/inputField/OutlinedInput`), which is right for a
 * form and 2px taller than an entire Compact row. Setting padding alone leaves
 * that 44px in place — measured 54px rendered, turning a 42px row into 62px.
 *
 * 32px clears the tallest thing already in a comfortable row (the 40px action
 * button), so turning edit mode on does not move the row at all.
 * `density="compact"` takes it lower still from `CustomTable`'s own rules.
 *
 * The theme also strips the outline (`notchedOutline: { border: none }`) and
 * fills with `background.paper`, which is the same white as the row — so the
 * field needs a border of its own here or there is nothing to show it is one.
 */
const FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    padding: 0,
    border: "1px solid",
    borderColor: "divider",
    "&.Mui-focused": { borderColor: "primary.main" },
    "&.Mui-error": { borderColor: "error.main" },
    "&.Mui-disabled": { borderStyle: "dashed", backgroundColor: "transparent" },
  },
  "& .MuiOutlinedInput-input": {
    padding: "0 10px",
    minHeight: "32px",
    fontSize: "0.8125rem",
    // The border fades out at all four corners without these two.
    //
    // The app's global `MuiInputBase` input slot gives the inner `<input>` its
    // own OPAQUE background and a 4px radius
    // (`theme/overridesComponets/inputField/inputBase`). The root here is
    // rounded to 8px and carries the border, and its padding is 0, so the input
    // sits flush against the border's inner edge — where its squarer, filled
    // corner paints over the inside of the 8px curve and thins the border to
    // nothing at each corner.
    //
    // The fill belongs to the root, so the input carries none. `inherit` keeps
    // the corner honest if a background ever comes back (autofill paints one
    // through a huge inset box-shadow, which the radius does clip).
    backgroundColor: "transparent",
    borderRadius: "inherit",
  },
} as const;

// ---------------------------------------------------------------------------
// the toolbar control
// ---------------------------------------------------------------------------

/** Sits beside the column picker, and is the same shape as it. */
export function TableEditToggle({ onClick }: { onClick: () => void }) {
  return (
    <CustomLoadingButton
      size="small"
      variant="outlined"
      Icon={EditOutlinedIcon}
      iconLocation="startIcon"
      isLoading={false}
      title="Edit"
      onClick={onClick}
    />
  );
}
